import React from 'react';
import Styled from 'styled';
import { useMutation } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import { ApolloQueryResult } from '@apollo/client';
import { Field, Form, Formik, FieldProps } from 'formik';

import { emailValidator } from 'four-nets/ui-v2/form/validators';
import Input from 'four-nets/ui-v2/form/input/input';
import Modal from 'four-nets/ui-v2/modal/modal';

import defaultStyles from './email-dialog.scss';
import mutation, { SetUpNewEmailMutation, SetUpNewEmailMutationVariables } from './mutation.graphql';
import {
    SettingsProfileAndAccountQuery,
    SettingsProfileAndAccountQueryVariables,
} from '../../settings-profile-and-account.graphql';

interface Props {
    styles: typeof defaultStyles;
    onClose: () => void;
    refetch: (
        variables?: SettingsProfileAndAccountQueryVariables | undefined
    ) => Promise<ApolloQueryResult<SettingsProfileAndAccountQuery>>;
    id: string;
    email: string;
    open: boolean;
}

interface Values {
    email: string;
}

const EmailDialog: React.FC<Props> = props => {
    const { styles, id, email, open, onClose, refetch } = props;

    const [setUpNewEmail] = useMutation<SetUpNewEmailMutation, SetUpNewEmailMutationVariables>(mutation);

    return (
        <Modal title={<FormattedMessage id='CHANGE_EMAIL' />} onClose={onClose} open={open}>
            <Formik<Values>
                initialValues={{
                    email: '',
                }}
                onSubmit={values => {
                    setUpNewEmail({
                        variables: {
                            input: {
                                id,
                                newEmail: values.email,
                            },
                        },
                    })
                        .then(() => {
                            refetch();
                            onClose();
                        })
                        .catch(error => {});
                }}
                render={({ isSubmitting, isValid }) => (
                    <Form className={styles.form}>
                        <FormattedMessage id='CURRENT_EMAIL'>
                            {msg => <span className={styles.label}>{msg}</span>}
                        </FormattedMessage>
                        <span className={styles.currentMail}>{email}</span>
                        <label>
                            <FormattedMessage id='NEW_EMAIL'>
                                {msg => <span className={styles.label}>{`${msg}*`}</span>}
                            </FormattedMessage>
                            <Field name='email' validate={emailValidator}>
                                {({ field }: FieldProps) => <Input {...field} />}
                            </Field>
                        </label>
                        <FormattedMessage id='EMAIL_CHANGE_INSTRUCTIONS'>
                            {msg => <span className={styles.instructions}>{`*${msg}`}</span>}
                        </FormattedMessage>
                        <button disabled={!isValid || isSubmitting} type='submit' className={styles.submitButton}>
                            <FormattedMessage id='SAVE_CHANGES' values={{ gender: 'v' }} />
                        </button>
                    </Form>
                )}
            />
        </Modal>
    );
};

export default Styled(defaultStyles)(EmailDialog);
